import { reactive } from "vue";

// One in-flight "add to cart" per product, shared by every Add to cart button
// that shows it. The purchase block and the sticky bar mount separate button
// instances, and the bar can unmount (scroll up) while its request is still
// out, handing the shopper the main button: a per-instance flag let that
// second tap add a second unit and send a second add_to_cart.
//
// Only a click ever claims a product, so the server render always reads
// "not pending" from this module-level state.

// A request that never settles must not lock the button for the visit.
export const ADD_TO_CART_GUARD_MAX_MS = 10_000;

const pending = reactive(new Set<string>());
const claims = new Map<
  string,
  { token: number; timer: ReturnType<typeof setTimeout> }
>();
let lastToken = 0;

export const addToCartKey = (product: unknown): string => {
  const id = (product as { id?: unknown } | null | undefined)?.id;
  return id === undefined || id === null ? "" : String(id);
};

export const isAddToCartPending = (key: string): boolean => pending.has(key);

/**
 * Claims `key` for one add. Returns its release function, or null when an add
 * for the same product is already in flight. A release that arrives after the
 * safety timer already freed the key (and a newer tap claimed it) is ignored.
 */
export function claimAddToCart(
  key: string,
  maxMs = ADD_TO_CART_GUARD_MAX_MS
): (() => void) | null {
  if (pending.has(key)) return null;
  const token = ++lastToken;
  const release = () => {
    const claim = claims.get(key);
    if (!claim || claim.token !== token) return;
    clearTimeout(claim.timer);
    claims.delete(key);
    pending.delete(key);
  };
  pending.add(key);
  claims.set(key, { token, timer: setTimeout(release, maxMs) });
  return release;
}
